// eslint-disable-next-line import/extensions
import 'groupby-polyfill/lib/polyfill.js'
import { useEffect, useState } from 'react'
import { Link, useFetcher, useFetchers, useSearchParams } from 'react-router'
import { ALL_GARMENTS, capitalize, formatDate, formatDateAPI, zonedDatetimeToDate } from '@trinity/utils'
import {
  Button,
  ClientAvatar,
  DatePicker,
  Dialog,
  Divider,
  ImageCards,
  MenuItem,
  OpenInNew,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '../../components'
import { useGlobalState } from '../../hooks'

interface CloudClosetProps {
  client: TrinityAPI.ClientExtendedType
  garmentTypes: TrinityAPI.GarmentTypeType[]
  garments: Record<string, TrinityAPI.GarmentRenderType[]>
  children?: React.ReactNode
}

export function CloudCloset({ client, garmentTypes, garments, children = null }: CloudClosetProps) {
  const { setToastInfo } = useGlobalState()
  const fetchers = useFetchers()
  const fetcher = useFetcher({ key: 'archive' })
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [confirm, setConfirm] = useState<TrinityAPI.SetHiddenGarmentParamsType | undefined>()
  const [confirmType, setConfirmType] = useState<'restore' | 'archive'>('restore')

  const handleClose = () => {
    setShowConfirmation(false)
    setConfirm(undefined)
  }
  const openConfirm = (params: TrinityAPI.SetHiddenGarmentParamsType, type: 'restore' | 'archive') => {
    setConfirm(params)
    setShowConfirmation(true)
    setConfirmType(type)
  }

  useEffect(() => {
    const fetcher = fetchers.find(f => f.key === ImageCards.FETCHER_KEY || f.key === 'archive')
    if (fetcher?.data) {
      setToastInfo({ show: true, message: fetcher.data.message, severity: 'success' })
    }
  }, [fetchers, setToastInfo])

  return (
    <>
      <Stack spacing={6}>
        <ClientAvatar client={client} />
        <Divider />
        {children}
        <CloudClosetFilters garmentTypes={garmentTypes} />
        <CloudClosetGarmentTypes garments={garments} openConfirm={openConfirm} />
      </Stack>
      <Dialog open={showConfirmation} onClose={handleClose}>
        <fetcher.Form method='POST'>
          <input type='hidden' name='id' value={confirm?.id} />
          <input type='hidden' name='isHidden' value={confirm?.isHidden} />
          <Stack spacing={4} p={4}>
            <Typography variant='h5'>Are you sure you want to {capitalize(confirmType)} this Order?</Typography>
            <Stack direction='row' spacing={2} justifyContent='flex-end'>
              <Button size='small' variant='outlined' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size='small'
                color={confirmType == 'restore' ? 'info' : 'error'}
                type='submit'
                name='intent'
                value={confirmType}
                onClick={handleClose}
              >
                {capitalize(confirmType)}
              </Button>
            </Stack>
          </Stack>
        </fetcher.Form>
      </Dialog>
    </>
  )
}

function CloudClosetFilters({ garmentTypes }: Pick<CloudClosetProps, 'garmentTypes'>) {
  const [params, setParams] = useSearchParams()
  const garmentType = params.get('garmentType') ?? 'all'
  const endDate = params.get('endDate') ? new Date(params.get('endDate') as string) : new Date()
  const startDate = params.get('startDate')
    ? new Date(params.get('startDate') as string)
    : zonedDatetimeToDate(Temporal.Now.zonedDateTimeISO().subtract({ years: 3 }))

  return (
    <Stack direction={{ mobile: 'column', laptop: 'row' }} spacing={4} justifyContent='space-between'>
      <TextField
        select
        label='Garment Type'
        value={garmentType}
        onChange={e =>
          setParams(params => {
            params.set('garmentType', e.target.value)
            return params
          })
        }
        sx={{ width: { mobile: 1, laptop: 1 / 5 } }}
      >
        <MenuItem value='all'>All</MenuItem>
        {ALL_GARMENTS.map(garmentType => (
          <MenuItem
            key={garmentType.bitmask}
            disabled={!garmentTypes.includes(garmentType.bitmask)}
            value={garmentType.bitmask}
          >
            {garmentType.alternateName ?? garmentType.formalName}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction='row' spacing={2}>
        <DatePicker
          label='From'
          defaultValue={startDate}
          onChange={newDate =>
            setParams(params => {
              if (newDate) {
                params.set('startDate', formatDateAPI(newDate))
              }
              return params
            })
          }
          slotProps={{
            textField: { helperText: 'MM/DD/YYYY', name: 'startDate' },
            openPickerIcon: { sx: { color: 'grey.900' } },
          }}
        />
        <DatePicker
          label='To'
          defaultValue={endDate}
          onChange={newDate =>
            setParams(params => {
              if (newDate) {
                params.set('endDate', formatDateAPI(newDate))
              }
              return params
            })
          }
          slotProps={{
            textField: { helperText: 'MM/DD/YYYY', name: 'endDate' },
            openPickerIcon: { sx: { color: 'grey.900' } },
          }}
        />
      </Stack>
    </Stack>
  )
}

interface CloudClosetGarmentTypesProps {
  garments: Record<string, TrinityAPI.GarmentRenderType[]>
  openConfirm: (params: TrinityAPI.SetHiddenGarmentParamsType, type: 'restore' | 'archive') => void
}
function CloudClosetGarmentTypes({ garments, openConfirm }: CloudClosetGarmentTypesProps) {
  const { onMobile } = useGlobalState()
  const garmentTypes = Object.keys(garments).sort()
  if (!garmentTypes.length) {
    return (
      <Typography variant='h5' align='center'>
        No Garments Found
      </Typography>
    )
  }

  return garmentTypes.map(garmentType => {
    const garmentItems = garments[garmentType] ?? []
    const firstItem = garmentItems?.[0]
    const garmentTypeDescription = firstItem?.garmentType?.alternateName ?? firstItem?.garmentType?.formalName
    const items = garmentItems.map(item => ({
      key: item.id,
      title: {
        text: item.fabric.trinityFabricNumber,
        link: `/fabric-explorer/fabrics/${item.fabric?.id}`,
        tooltip: 'View on Fabric Explorer',
      },
      image: {
        uri: item.uri ? `${item.uri}&width=500` : `${item.garmentType.fallbackImage}&width=500`,
        zoom_uri: item.uri ?? undefined,
        description: item.fabric.description,
      },
      actions: [createAction(item, openConfirm)],
      item,
    }))

    return (
      <Stack p={2} key={garmentType} spacing={4}>
        <Typography variant='h4'>{garmentTypeDescription}</Typography>
        <ImageCards items={items}>
          {item => (
            <Stack p={2}>
              <Stack
                spacing={2}
                pb={2}
                sx={{ '& .MaterialRow:not(:last-child)': { borderBottom: 1, borderColor: 'grey.200' } }}
              >
                {item.lining && (
                  <MaterialRow
                    label='Lining'
                    description={item.lining.trinityFabricNumber}
                    link={`/fabric-explorer/fabrics/${item.lining.id}`}
                  />
                )}
                {item.button && <MaterialRow label='Button' description={item.button.description} />}
                <MaterialRow label='Ordered' description={formatDate(item.createdAt)} />
                {!onMobile && (
                  <MaterialRow
                    label='Garment ID'
                    description={item.title}
                    link={`/orders/${item.orderId}`}
                    tooltip='View Order details'
                  />
                )}
              </Stack>
            </Stack>
          )}
        </ImageCards>
      </Stack>
    )
  })
}

/** HELPERS */
interface MaterialRowProps {
  label: string
  description: string | number
  tooltip?: string
  link?: string
  newTab?: boolean
}
const MaterialRow = ({
  label,
  description,
  link = undefined,
  newTab = false,
  tooltip = undefined,
}: MaterialRowProps) => {
  return (
    <Stack direction='row' justifyContent='space-between' className='MaterialRow'>
      <Typography variant='smallBody2'>{label}</Typography>
      <Tooltip title={tooltip ?? description}>
        <Typography
          variant='smallBody1'
          noWrap
          width='10rem'
          align='right'
          sx={{ '& a': { color: 'primary.main', textDecoration: 'none' } }}
        >
          {link ? (
            <Link to={link} target={newTab ? '_blank' : undefined}>
              {description}
              {newTab && (
                <Typography
                  variant='link'
                  sx={{
                    fontSize: '0.875rem',
                    color: 'primary.main',
                    '& .MuiSvgIcon-root': { verticalAlign: 'bottom', width: '1rem', height: '1rem' },
                  }}
                >
                  <OpenInNew />
                </Typography>
              )}
            </Link>
          ) : (
            <>{description}</>
          )}
        </Typography>
      </Tooltip>
    </Stack>
  )
}

const createAction = (
  item: TrinityAPI.GarmentRenderType,
  openConfirm: (params: TrinityAPI.SetHiddenGarmentParamsType, type: 'restore' | 'archive') => void,
) => {
  if (item.isHidden) {
    return {
      type: 'restore',
      title: 'Restore',
      callback: () => openConfirm({ id: item.id, isHidden: '0' }, 'restore'),
    } as const
  }

  return {
    type: 'delete',
    title: 'Archive',
    callback: () => openConfirm({ id: item.id, isHidden: '1' }, 'archive'),
  } as const
}
